import {
  createHighlighter,
  type Highlighter,
  type BundledLanguage,
} from 'shiki';

let highlighter: Highlighter | null = null;

// Languages commonly used in tutorials
const supportedLanguages: BundledLanguage[] = [
  'typescript',
  'javascript',
  'html',
  'css',
  'json',
  'bash',
  'shell',
  'markdown',
];

/**
 * Initialize the Shiki highlighter
 * Should be called once when the app starts
 */
export async function initHighlighter(): Promise<void> {
  if (highlighter) return;

  highlighter = await createHighlighter({
    themes: ['github-dark', 'github-light'],
    langs: supportedLanguages,
  });
}

/**
 * Get the current highlighter instance
 */
export function getHighlighter(): Highlighter | null {
  return highlighter;
}

/**
 * Check if highlighter is ready
 */
export function isHighlighterReady(): boolean {
  return highlighter !== null;
}

/**
 * Highlight code and return HTML string
 */
export function highlightCode(
  code: string,
  language: string = 'typescript'
): string {
  if (!highlighter) {
    // eslint-disable-next-line no-console
    console.warn('Highlighter not initialized');
    return escapeHtml(code);
  }

  // Map common language aliases
  const langMap: Record<string, BundledLanguage> = {
    ts: 'typescript',
    js: 'javascript',
    sh: 'bash',
    zsh: 'bash',
  };

  const lang = (langMap[language] || language) as BundledLanguage;

  // Check if language is supported
  if (!supportedLanguages.includes(lang)) {
    return escapeHtml(code);
  }

  try {
    return highlighter.codeToHtml(code, {
      lang,
      themes: {
        light: 'github-light',
        dark: 'github-dark',
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to highlight code with language ${lang}:`, error);
    return escapeHtml(code);
  }
}

/**
 * Apply syntax highlighting to all code blocks in a container
 */
export function highlightCodeBlocks(container: HTMLElement): void {
  if (!highlighter) {
    // eslint-disable-next-line no-console
    console.warn('Highlighter not initialized');
    return;
  }

  const codeBlocks = container.querySelectorAll('pre code');

  codeBlocks.forEach((codeElement) => {
    const code = codeElement.textContent || '';
    const classNames = codeElement.className;
    const langMatch = classNames.match(/language-(\w+)/);
    const language = langMatch ? langMatch[1] : 'typescript';

    const highlighted = highlightCode(code, language);

    // Replace the pre element with the highlighted version
    const preElement = codeElement.parentElement;
    if (preElement && preElement.tagName === 'PRE') {
      // Create a wrapper to hold the highlighted code
      const wrapper = document.createElement('div');
      wrapper.innerHTML = highlighted;
      const newPre = wrapper.firstElementChild;

      if (newPre) {
        // Preserve original classes
        newPre.classList.add(
          'rounded-lg',
          'overflow-x-auto',
          'text-sm',
          'leading-relaxed',
          'border',
          'border-slate-700'
        );

        preElement.replaceWith(newPre);
      }
    }
  });
}

/**
 * Escape HTML entities for safe display
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}
